import { useEffect, useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { X } from "lucide-react";
import QuickQuoteForm from "@/components/QuickQuoteForm";

/**
 * ScrollIntentCTA
 *
 * Slides in a small dismissable card once the user has scrolled past 60% of
 * the page. Now includes an inline QuickQuoteForm so users can convert
 * without a route change.
 *
 * - Hides on /book, /contact, /admin*, /unsubscribe, /beat-my-estimate
 * - Hides on screens < md (mobile already has StickyMobileCTA + ExitIntent)
 * - Persists dismissal for the session (sessionStorage)
 */
const DISMISS_KEY = "scrollIntentCtaDismissed";

const ScrollIntentCTA = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setVisible(false);
    setExpanded(false);
    try {
      const stored = sessionStorage.getItem(DISMISS_KEY);
      setDismissed(stored === "1");
    } catch {
      setDismissed(false);
    }
  }, [pathname]);

  useEffect(() => {
    if (dismissed) return;
    if (
      pathname.startsWith("/book") ||
      pathname.startsWith("/contact") ||
      pathname.startsWith("/admin") ||
      pathname.startsWith("/unsubscribe") ||
      pathname.startsWith("/beat-my-estimate")
    ) {
      return;
    }

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = doc.scrollHeight;
      if (total <= 0) return;
      const ratio = scrolled / total;
      if (ratio > 0.6) setVisible(true);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname, dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  if (!visible || dismissed) return null;

  return (
    <div
      className={`hidden md:block fixed bottom-6 right-6 z-40 ${
        expanded ? "max-w-md" : "max-w-sm"
      } bg-background text-foreground border border-primary/30 rounded-xl shadow-2xl animate-in slide-in-from-right-4 fade-in duration-300`}
      role="complementary"
      aria-label="Quick quote prompt"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 z-10 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      {expanded ? (
        <div className="p-4 pt-8">
          <QuickQuoteForm
            source="scroll-intent"
            variant="stacked"
            tone="white"
            compact
            heading="Get your free quote"
            subheading="A real fence expert will reach out within 1 business day."
          />
        </div>
      ) : (
        <div className="p-4 pr-9">
          <p className="font-semibold text-sm mb-0.5">Get a custom quote in 24 hours</p>
          <p className="text-muted-foreground text-xs mb-2 leading-snug">
            Free virtual consultation, no pressure — your timeline is confirmed in your written proposal.
          </p>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-1.5 transition-all"
          >
            Get my quote →
          </button>
        </div>
      )}
    </div>
  );
};

export default ScrollIntentCTA;
